import React, { useEffect, useState, useRef } from "react";
import { Button } from "@repo/ui";

import { useParams, useNavigate } from "react-router-dom";
import { colors } from "@repo/ui";
import BackButton from "../../../../../components/BackButton";
import PageContainer from "../../../../../components/PageContainer/PageContainer";
import { Gift } from "../../../../../services/giftApiService";
import { Product } from "../../../../../services/productApiService";
import { fetchGiftData, formatDate, ThankYouMessage } from "../common";
import placeholderImage from "../../../../../assets/placeholders/picture.webp";
import { useTranslation } from "react-i18next";
import { withTranslations } from "../../../../../hoc/withTranslations/withTranslations";

const LOCALE =
  "modules_MyGiftTable_components_GiftDetail_mobile_MobileGiftDetail";

const GiftDetailMobile: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const navigate = useNavigate();
  const { giftId } = useParams<{ giftId: string }>();
  const [gift, setGift] = useState<Gift | null>(null);
  const [mediaURL, setMediaURL] = useState<string>(placeholderImage);
  const [isVideo, setIsVideo] = useState(false);
  const [productDetails, setProductDetails] = useState<{
    [id: string]: Product;
  }>({});
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (giftId) {
      fetchGiftData(
        giftId,
        setGift,
        setMediaURL,
        setIsVideo,
        setProductDetails
      );
    }
  }, [giftId]);

  const handleVideoClick = () => {
    videoRef.current
      ?.requestFullscreen()
      .catch((error) => console.error(t("videoFullscreenError"), error));
  };

  if (!gift) {
    return <div>{t("loading")}</div>;
  }

  return (
    <PageContainer startColor={colors.background} endColor="white">
      <div className="mt-16"></div>
      <div className="p-4">
        <BackButton
          text={t("backToReceivedGifts")}
          onClick={() => navigate("/my-table/gifts")}
        />
        <div className="flex flex-col items-center mt-4 space-y-4 bg-white p-6">
          {/* Media (image or video) */}
          <div className="w-48 h-48 flex items-center justify-center mb-4">
            {isVideo ? (
              <video
                src={mediaURL}
                controls
                ref={videoRef}
                onClick={handleVideoClick}
                className="object-cover h-full w-full rounded"
              >
                {t("videoNotSupported")}
              </video>
            ) : (
              <img
                src={mediaURL || placeholderImage}
                alt={t("giftMediaAlt")}
                className="object-cover h-full w-full rounded-full"
              />
            )}
          </div>

          {/* Donator Info */}
          <p className="text-gray-600 font-bold text-lg mb-2">
            {t("sentBy")}:{" "}
            {gift.IsAnonymous ? t("anonymous") : gift.DonatorName}
          </p>
          <p className="text-gray-500 font-bold mb-4">
            {t("receivedOn")}: {formatDate(gift.GivenDate)}
          </p>

          {/* Gift message */}
          <p className="text-primary italic border-l-4 border-primary pl-4 mb-4">
            {gift.Message || t("noMessage")}
          </p>

          {/* Total Value */}
          <div className="text-gray-800 font-bold text-lg mb-6">
            {t("total")}: ${gift.Value?.toLocaleString()}
          </div>

          {/* Products */}
          <div className="w-full space-y-4">
            <h3 className="text-lg font-semibold">{t("giftProducts")}</h3>
            {gift.Products.map((product) => {
              const productDetail = productDetails[product.ID];
              return (
                <div
                  key={product.ID}
                  className="flex items-center space-x-4 p-4 border rounded-lg"
                >
                  <img
                    src={productDetail?.pictures?.[0] || placeholderImage}
                    alt={productDetail?.title || t("productAlt")}
                    className="w-24 h-24 object-cover rounded-full"
                  />
                  <div className="flex flex-col">
                    <p className="text-gray-700 font-bold">
                      {productDetail?.title}
                    </p>
                    <p className="text-gray-500">
                      {t("quantity")}: {product.Quantity}
                    </p>
                    <p className="text-gray-800 font-semibold">
                      {t("price")}: ${product.Value?.toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Thank You Message (conditional) */}
          <div className="w-full space-y-4 mt-4">
            {gift.ThankYouMessage?.trim() ? (
              <>
                <h3 className="text-lg font-semibold mb-2">
                  {t("youResponded")}:
                </h3>
                <ThankYouMessage message={gift.ThankYouMessage} />
              </>
            ) : (
              <div className="text-center mt-8 space-y-4">
                <p className="text-gray-600 leading-relaxed">
                  {t("noThankYouMessage")}
                </p>
                <Button
                  className="mx-auto"
                  type="primary"
                  onClick={() =>
                    navigate(`/my-table/gift/${giftId}/thankyoumessage`)
                  }
                >
                  {t("sendItNow")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default withTranslations(LOCALE)(GiftDetailMobile);
